import { alpha, Box, Divider, Stack, Tab as MuiTab, Tabs as MuiTabs, Typography, useMediaQuery } from '@mui/material';
import { ChangeEvent, ReactNode } from 'react';
import { squareOnStickyHeaderStyle, stickyTopBarProps } from './useStickyTableEdges';

export const STICKY_TOP = 48;
export const BAR_HEIGHT = 56;
const HEADER_MIN_HEIGHT = 140;

export const HeaderTabs = (props: {
  value: number;
  children: ReactNode;
  onChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  darkMode?: boolean;
}) => {
  const { darkMode, ...rest } = props;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  return (
    <MuiTabs
      sx={(theme) => ({
        minHeight: 36,
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        '& .MuiTabs-scrollButtons': {
          color: darkMode ? theme.palette.common.white : theme.palette.primary.main,
          '& svg': {
            width: '1.5rem',
            height: '1.5rem',
          },
        },
      })}
      {...rest}
      variant="scrollable"
      allowScrollButtonsMobile
      scrollButtons={isMobile}
      slotProps={{
        indicator: { children: <span /> },
      }}
    />
  );
};

export const HeaderTab = ({
  divider = 'none',
  darkMode,
  ...props
}: {
  label: string;
  selected?: boolean;
  divider: 'left' | 'none';
  darkMode?: boolean;
}) => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      flex: 1,
      textAlign: 'center',
      backgroundColor: theme.palette.primary.main,
      [theme.breakpoints.down('md')]: {
        backgroundColor: darkMode ? theme.palette.primary.main : theme.palette.common.white,
        borderBottom: 'none',
        paddingBottom: 0,
      },
    })}
  >
    {divider === 'left' && (
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={(theme) => ({ backgroundColor: theme.palette.grey[300], marginX: 1 })}
      />
    )}
    <MuiTab
      sx={(theme) => ({
        flex: 1,
        minWidth: 72,
        minHeight: 36,
        padding: theme.spacing(0, 2),
        textTransform: 'none',
        backgroundColor: 'inherit',
        color: theme.palette.getContrastText(theme.palette.primary.main),
        fontWeight: 400,
        fontSize: theme.typography.pxToRem(15),
        margin: theme.spacing(0, 0.25),
        whiteSpace: 'nowrap',
        borderRadius: theme.spacing(2.5),
        transition: 'all 100ms ease-in-out',
        '&:hover, &:focus': {
          backgroundColor: alpha(theme.palette.common.white, 0.1),
        },
        [theme.breakpoints.down('md')]: {
          color: theme.palette.getContrastText(darkMode ? theme.palette.primary.main : theme.palette.common.white),
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.background.default,
          color: `${theme.palette.text.primary} !important`,
        },
      })}
      {...props}
    />
  </Box>
);

export enum AfterActionsVariant {
  NoStyle = 'nostyle',
  Basic = 'basic',
}

// How the cover art fills its box. A strip is a wide image sized by height and
// anchored right, ending short of a 60% box so the primary strip shows through. A
// scene fills a box a little over half the header so its visible art is the same
// width at any header height, cropping the foreground rather than leaving a gap; its
// swish may stretch for a gentler sweep into the sky, and a 2px primary inset hides
// the anti-aliased left edge that would otherwise read as a hairline.
const COVER_LAYOUTS = {
  strip: { width: '60%', backgroundSize: 'auto 100%', swishSize: 'auto 100%', leftEdgeInsetPx: 0 },
  scene: { width: '56%', backgroundSize: 'cover', swishSize: '55% 100%', leftEdgeInsetPx: 2 },
};

export type PageHeaderCover = {
  image: string;
  // The art that blends the cover into the primary strip.
  swish: string;
  layout: keyof typeof COVER_LAYOUTS;
};

export type SearchPlacement = 'topRight' | 'bottomLeft' | 'bottomRight';

const BottomRow = ({
  rightSection,
  onTabChange,
  tabs,
  tab,
  actions,
  newTabGroups,
}: {
  rightSection: ReactNode;
  onTabChange?: (event: ChangeEvent<{}>, newValue: number) => void;
  tabs?: string[];
  tab?: number;
  actions?: ReactNode;
  newTabGroups?: boolean[];
}) => (
  <Stack
    direction="row"
    sx={{
      justifyContent: 'space-between',
      alignItems: 'end',
      flex: 1,
      marginBottom: 0.5,
      zIndex: 2,
    }}
  >
    {actions}
    {/* Bottom Left */}
    <Stack direction="row">
      {onTabChange && tabs && (
        <HeaderTabs value={tab ?? 0} onChange={(event, val) => onTabChange(event, val ?? 0)}>
          {tabs?.map((label: string, index) => (
            <HeaderTab
              key={label}
              label={label}
              divider={newTabGroups && newTabGroups[index] && index > 0 ? 'left' : 'none'}
            />
          ))}
        </HeaderTabs>
      )}
    </Stack>
    {/* Bottom Right */}
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1,
      }}
    >
      {rightSection}
    </Stack>
  </Stack>
);

const PageHeader = ({
  title,
  subtitle,
  styleVariant = 'primary',
  cover,
  decoration,
  tabs,
  tab,
  onTabChange,
  newTabGroups,
  actions,
  topRight,
  search,
  beforeSearch,
  afterSearch,
  searchPlacement = 'bottomRight',
  afterActions,
  isSticky,
}: {
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  styleVariant?: 'primary' | 'plain';
  cover?: PageHeaderCover;
  // Shown above the header, overlapping its top edge.
  decoration?: ReactNode;
  tabs?: string[];
  tab?: number;
  onTabChange?: (event: ChangeEvent<{}>, newValue: number) => void;
  newTabGroups?: boolean[];
  actions?: ReactNode;
  // Moves to the bottom right when the search sits top right.
  topRight?: ReactNode;
  search?: ReactNode;
  beforeSearch?: ReactNode;
  afterSearch?: ReactNode;
  searchPlacement?: SearchPlacement;
  afterActions?: { node: ReactNode; variant?: AfterActionsVariant };
  isSticky?: boolean;
}) => {
  const afterActionsVariant =
    afterActions?.variant ?? (afterActions ? AfterActionsVariant.NoStyle : AfterActionsVariant.Basic);

  const coverLayout = COVER_LAYOUTS[cover?.layout ?? 'strip'];
  const coverBackdrop = !!cover && (
    <Stack
      sx={(theme) => ({
        position: 'absolute',
        width: coverLayout.width,
        height: '100%',
        top: 0,
        right: 0,
        display: 'flex',
        backgroundImage: `url(${cover.image})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top right',
        backgroundSize: coverLayout.backgroundSize,
        boxShadow: coverLayout.leftEdgeInsetPx
          ? `inset ${coverLayout.leftEdgeInsetPx}px 0 0 ${theme.palette.primary.main}`
          : undefined,
        borderRadius: afterActions ? theme.spacing(1, 1, 0, 0) : theme.spacing(1),
      })}
    >
      <Stack
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundImage: `url(${cover.swish})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'left',
          backgroundSize: coverLayout.swishSize,
        }}
      />
    </Stack>
  );

  const searchGroup = (
    <>
      {beforeSearch}
      {search}
      {afterSearch}
    </>
  );

  const titleRow = (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'start',
        width: '100%',
        flex: 1,
      }}
    >
      {/* Top Left */}
      <Stack sx={(theme) => ({ marginTop: theme.spacing(1), zIndex: 53 })}>
        {typeof title === 'string' ? (
          <Typography
            variant={styleVariant === 'plain' ? 'h5' : 'h4'}
            sx={(theme) => ({
              color: styleVariant === 'plain' ? 'primary.text' : 'common.white',
              marginBottom: theme.spacing(0.5),
            })}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        {typeof subtitle === 'string' ? (
          <Typography variant={'body1'} sx={{ color: styleVariant === 'plain' ? 'primary.text' : 'common.white' }}>
            {subtitle}
          </Typography>
        ) : (
          subtitle
        )}
      </Stack>

      {/* Top Right */}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'end',
          flex: 1,
        }}
      >
        {searchPlacement === 'topRight' ? (
          <>
            {beforeSearch}
            {!!search && <Box sx={(theme) => ({ marginLeft: theme.spacing(1) })}>{search}</Box>}
            {afterSearch}
          </>
        ) : (
          topRight
        )}
      </Stack>
    </Stack>
  );

  const bottomRow = (
    <BottomRow
      tab={tab}
      tabs={tabs}
      onTabChange={onTabChange}
      newTabGroups={newTabGroups}
      actions={
        searchPlacement === 'bottomLeft' ? (
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center',
            }}
          >
            {actions}
            <Box>{searchGroup}</Box>
          </Stack>
        ) : (
          actions
        )
      }
      rightSection={searchPlacement === 'bottomRight' ? searchGroup : topRight}
    />
  );

  const afterActionsNode = <Box sx={{ width: '100%', paddingTop: 0 }}>{afterActions?.node}</Box>;

  return (
    <>
      <Box
        sx={(theme) => ({
          marginBottom: isSticky ? 0 : theme.spacing(1),
          position: 'relative',
          zIndex: 52,
          background: theme.palette.background.default,
          borderRadius: theme.spacing(1),
        })}
      >
        {decoration}
        <Stack
          sx={(theme) => ({
            position: 'relative',
            backgroundColor: styleVariant === 'plain' ? 'initial' : theme.palette.primary.main,
            borderRadius:
              afterActionsVariant === AfterActionsVariant.NoStyle ? theme.spacing(1, 1, 0, 0) : theme.spacing(1),
          })}
        >
          <Stack
            sx={[
              {
                gap: 1,
                justifyContent: 'space-between',
              },
              (theme) => ({
                padding: styleVariant === 'plain' ? 0 : theme.spacing(1, 2, isSticky ? 0 : 0.5, 3),
                borderRadius:
                  afterActionsVariant === AfterActionsVariant.NoStyle ? theme.spacing(1, 1, 0, 0) : theme.spacing(1),
                minHeight: styleVariant === 'plain' ? 'auto' : HEADER_MIN_HEIGHT,
              }),
            ]}
          >
            {coverBackdrop}
            {titleRow}
            {!isSticky && bottomRow}
          </Stack>
        </Stack>
        {!isSticky && afterActionsNode}
      </Box>
      {isSticky && (
        <>
          <Box
            {...stickyTopBarProps}
            sx={[
              (theme) => ({
                position: 'sticky',
                top: `${STICKY_TOP}px`,
                height: `${BAR_HEIGHT}px`,
                marginTop: `-${BAR_HEIGHT}px`,
                zIndex: 51,
                pointerEvents: 'none',
                background: styleVariant === 'plain' ? theme.palette.background.default : theme.palette.primary.main,
                borderRadius: afterActions ? 0 : theme.spacing(0, 0, 1, 1),
              }),
              squareOnStickyHeaderStyle,
            ]}
          />
          <Box
            {...stickyTopBarProps}
            sx={(theme) => ({
              position: 'sticky',
              top: `${STICKY_TOP}px`,
              marginTop: `-${BAR_HEIGHT}px`,
              marginBottom: theme.spacing(1),
              zIndex: 53,
            })}
          >
            <Stack sx={(theme) => ({ padding: styleVariant === 'plain' ? 0 : theme.spacing(1, 2, 0.5, 3) })}>
              {bottomRow}
            </Stack>
            {!!afterActions && (
              <Box
                sx={[
                  (theme) => ({
                    background: theme.palette.background.default,
                    borderRadius: theme.spacing(0, 0, 1, 1),
                  }),
                  squareOnStickyHeaderStyle,
                ]}
              >
                {afterActionsNode}
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default PageHeader;
